/**
 * LoadingSpinner.tsx
 * AMÉLIORATION: Composant réutilisable pour les indicateurs de chargement
 * Supporte différents styles et tailles
 */
import React, { memo } from 'react';

interface LoadingSpinnerProps {
  size?: 'sm' | 'md' | 'lg';
  variant?: 'default' | 'primary' | 'secondary';
  text?: string;
  fullScreen?: boolean;
  overlay?: boolean;
}

/**
 * Composant d'indicateur de chargement
 * Phase 3.1 : Optimisé avec React.memo
 */
const LoadingSpinnerComponent: React.FC<LoadingSpinnerProps> = ({
  size = 'md',
  variant = 'default',
  text,
  fullScreen = false,
  overlay = false,
}) => {
  const sizeMap = {
    sm: '16px',
    md: '24px',
    lg: '32px',
  };

  const colorMap = {
    default: 'var(--color-text-secondary, #6b7280)',
    primary: 'var(--color-primary, #3b82f6)',
    secondary: 'var(--color-text-primary, #1e293b)',
  };

  const spinnerSize = sizeMap[size];
  const spinnerColor = colorMap[variant];

  const spinner = (
    <div
      className="loading-spinner"
      style={{
        display: 'inline-flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: text ? 'var(--spacing-xs, 4px)' : 0,
      }}
    >
      <svg
        width={spinnerSize}
        height={spinnerSize}
        viewBox="0 0 24 24"
        fill="none"
        stroke={spinnerColor}
        strokeWidth="2"
        style={{
          animation: 'spin 1s linear infinite',
        }}
      >
        <path d="M21 12a9 9 0 11-6.219-8.56" />
      </svg>
      {text && (
        <span
          style={{
            fontSize: 'var(--font-size-xs, 12px)',
            color: 'var(--color-text-secondary, #6b7280)',
          }}
        >
          {text}
        </span>
      )}
    </div>
  );

  if (fullScreen || overlay) {
    return (
      <div
        className="loading-spinner-overlay"
        style={{
          position: fullScreen ? 'fixed' : 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: overlay ? 'rgba(255, 255, 255, 0.9)' : 'transparent',
          backdropFilter: overlay ? 'blur(4px)' : 'none',
          zIndex: 10000,
        }}
      >
        {spinner}
      </div>
    );
  }

  return spinner;
};

export const LoadingSpinner = memo(LoadingSpinnerComponent);
export default LoadingSpinner;
